import {
  AccountState,
  AccountType,
  isAccount,
  isRevisionByClientName,
} from '@mkp/account/data-access';
import { MenuRole } from '@mkp/account/models';
import { LegalEntityState, RevisionByClientName } from '@mkp/shared/models';
import { LoadingState } from '@mkp/shared/util-state';
import { selectIsBoUser } from '@mkp/user/state-logged-in/selectors';
import { Vacancy } from '@mkp/vacancy/models';
import { createSelector } from '@ngrx/store';
import { accountFeature } from './account.reducer';

const {
  selectEntities: selectAccountEntities,
  selectSelectedAccountId,
  selectAll: selectAllAccounts,
  selectFetchState,
} = accountFeature;

export { selectAccountEntities, selectAllAccounts, selectSelectedAccountId };

export const selectActiveAccounts = createSelector(selectAllAccounts, (accounts) =>
  accounts.filter((account) => account.state === AccountState.Active)
);

export const selectAccountById = (accountId: string | undefined) =>
  createSelector(accountFeature.selectEntities, (accounts) =>
    accountId ? accounts[accountId] : undefined
  );

export const selectSelectedAccount = createSelector(
  selectAccountEntities,
  selectSelectedAccountId,
  (entities, selectedAccountId) => (selectedAccountId ? entities[selectedAccountId] : undefined)
);

export const selectIsNetworkAccount = createSelector(
  selectSelectedAccount,
  (account) => account?.type === AccountType.Network
);

export const selectIsJoinAccount = createSelector(selectSelectedAccount, (selectedAccount) =>
  selectedAccount
    ? isRevisionByClientName(selectedAccount, RevisionByClientName.JoinImporter)
    : undefined
);

export const selectSelectedAccountMenuRoles = createSelector(
  selectSelectedAccount,
  (selectedAccount) => selectedAccount?._links.self.menu
);

export const selectHasMenuRole = (menuRole: MenuRole | undefined) =>
  createSelector(selectSelectedAccount, (selectedAccount) =>
    !selectedAccount
      ? undefined
      : !!selectedAccount._links.self.menu.find((role) => role === menuRole)
  );

export const selectLegalEntityNameFromVacancy = (vacancy: Vacancy) =>
  createSelector(selectLegalEntityFromVacancy(vacancy), (legalEntity) => legalEntity?.name);

export const selectAccountsByIds = (accountIds: string[]) =>
  createSelector(selectAccountEntities, (accounts) =>
    accountIds.map((accountId) => accounts[accountId]).filter(Boolean)
  );

export const selectHasSelectedAccount = createSelector(
  selectSelectedAccount,
  (selectSelectedAccount) => !!selectSelectedAccount
);

export const selectIsLegalEntityBeingVerified = createSelector(
  selectSelectedAccount,
  // selectedAccount can be null for multi-user accounts. We return false in this case
  (account): boolean =>
    isAccount(account) &&
    [LegalEntityState.InVerification, LegalEntityState.OnHold].includes(account.legalEntity.state)
);

export const selectAccountIsLoading = createSelector(
  selectFetchState,
  (fetchState) => fetchState === LoadingState.LOADING
);

export const selectAccountAtsName = createSelector(
  selectSelectedAccount,
  (account) => account?.settings.ats.name
);

export const selectHasAnyAccountWithMfaEnabled = createSelector(selectAllAccounts, (accounts) =>
  accounts.some((account) => account.settings.mfaEnabled)
);

export const selectFreeProductsAllowed = createSelector(
  selectSelectedAccount,
  (account) => account?.settings.freeProductsAllowed
);

const selectAccountFromVacancy = ({ accountId }: Vacancy) => selectAccountById(accountId);
const selectLegalEntityFromVacancy = (vacancy: Vacancy) =>
  createSelector(selectAccountFromVacancy(vacancy), (account) => account?.legalEntity);

export const selectIsSelectedAccountBeingVerified = createSelector(
  selectIsBoUser,
  selectIsLegalEntityBeingVerified,
  // never display "account is being verified" features for multi-account users
  (isMultipleAccountsUser, isLegalEntityBeingVerified) =>
    !isMultipleAccountsUser && isLegalEntityBeingVerified
);

export const selectAccountVerificationStatus = createSelector(
  selectIsSelectedAccountBeingVerified,
  selectSelectedAccountId,
  (isBeingVerified, accountId) => ({
    isBeingVerified,
    accountId,
  })
);
